import { APIEndpoint } from ".";

const token = localStorage.getItem("token");

export const getSnapTradeUsers = async (includeConnections?: boolean) => {
  const qs = includeConnections ? "?includeConnections=true" : "";
  const response = await fetch(`${APIEndpoint}/admin/snaptrade/users${qs}`, {
    method: "GET",
    headers: { Authorization: token || "" },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to load SnapTrade users");
  return data as { success: boolean; data: any[] };
};

export const getUserSnapTradeDetail = async (userId: number) => {
  const response = await fetch(`${APIEndpoint}/admin/snaptrade/users/${userId}`, {
    method: "GET",
    headers: { Authorization: token || "" },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to load user connections");
  return data as { success: boolean; data: { connections: any[]; accounts: any[] } };
};

export const deleteUserSnapTrade = async (userId: number) => {
  const response = await fetch(`${APIEndpoint}/admin/snaptrade/users/${userId}`, {
    method: "DELETE",
    headers: { Authorization: token || "" },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as any)?.message || "Failed to delete user's SnapTrade profile");
  }
  return true;
};

export const getUserAccountBalancesAdmin = async (userId: number, accountId: string) => {
  const response = await fetch(`${APIEndpoint}/admin/snaptrade/users/${userId}/accounts/${accountId}/balances`, {
    method: "GET",
    headers: { Authorization: token || "" },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to load account balances");
  return data as { success: boolean; data: any[] };
};

export const getUserAccountActivitiesAdmin = async (
  userId: number,
  accountId: string,
  filters?: { startDate?: string; endDate?: string; limit?: number; offset?: number; type?: string }
) => {
  const params = new URLSearchParams();
  if (filters?.startDate) params.set("startDate", filters.startDate);
  if (filters?.endDate) params.set("endDate", filters.endDate);
  if (filters?.limit != null) params.set("limit", String(filters.limit));
  if (filters?.offset != null) params.set("offset", String(filters.offset));
  if (filters?.type) params.set("type", filters.type);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const response = await fetch(`${APIEndpoint}/admin/snaptrade/users/${userId}/accounts/${accountId}/activities${qs}`, {
    method: "GET",
    headers: { Authorization: token || "" },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to load account activities");
  return data as { success: boolean; data: any[] };
};

export default { getSnapTradeUsers, getUserSnapTradeDetail, deleteUserSnapTrade, getUserAccountBalancesAdmin, getUserAccountActivitiesAdmin };

export interface StripeDispute {
  id: string;
  amount: number;
  currency: string;
  reason: string;
  status: string;
  created: number;
  charge?: string | { id: string; customer?: string; amount?: number; description?: string };
  evidence?: Record<string, string | null>;
  evidence_details?: { due_by?: number; past_due?: boolean; submission_count?: number; has_evidence?: boolean };
}

const adminStripeRequest = async (path: string, init: RequestInit = {}) => {
  const response = await fetch(`${APIEndpoint}/admin/stripe/disputes${path}`, {
    ...init,
    headers: { Authorization: localStorage.getItem("token") || "", "Content-Type": "application/json", ...(init.headers || {}) },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = response.status === 404 && path.includes("/ai-evidence")
      ? "the AI evidence endpoint is not deployed on the configured production API (HTTP 404)"
      : data?.message || data?.error || `HTTP ${response.status}`;
    throw new Error(`Stripe dispute request failed: ${detail}`);
  }
  return data;
};

export const listStripeDisputes = (filters: { limit?: number; startingAfter?: string; createdAfter?: string; createdBefore?: string; id?: string } = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, String(value)); });
  return adminStripeRequest(`?${params.toString()}`) as Promise<{ success: boolean; data: StripeDispute[]; hasMore: boolean; nextCursor?: string }>;
};

export const getStripeDispute = (id: string) => adminStripeRequest(`/${encodeURIComponent(id)}`) as Promise<{ success: boolean; dispute: StripeDispute; related: { customer?: { email?: string; name?: string }; user?: { email?: string }; cancellations: any[] } }>;
export const generateStripeDisputeEvidence = (id: string, sourceUrls: string[] = []) => adminStripeRequest(`/${encodeURIComponent(id)}/ai-evidence`, { method: "POST", body: JSON.stringify({ sourceUrls }) }) as Promise<{ success: boolean; suggestion: { evidence: Record<string, string>; sources: Array<{ id: string; label: string; fields: string[] }>; missingFields: string[]; warnings: string[] } }>;

export const saveStripeDisputeEvidence = (id: string, evidence: Record<string, string>) => adminStripeRequest(`/${encodeURIComponent(id)}/evidence`, { method: "PATCH", body: JSON.stringify({ evidence }) });
export const submitStripeDispute = (id: string, evidence: Record<string, string>) => adminStripeRequest(`/${encodeURIComponent(id)}/submit`, { method: "POST", body: JSON.stringify({ evidence }) });
export const acceptStripeDispute = (id: string) => adminStripeRequest(`/${encodeURIComponent(id)}/accept`, { method: "POST" });
export const uploadStripeDisputeFile = async (id: string, file: File) => {
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(`${APIEndpoint}/admin/stripe/disputes/${encodeURIComponent(id)}/files`, { method: "POST", headers: { Authorization: localStorage.getItem("token") || "" }, body: form });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.message || "Evidence upload failed");
  return data;
};

export interface CancellationFeedbackFilters {
  page?: number;
  pageSize?: number;
  primaryReason?: string;
  allowFollowUp?: boolean;
  createdAfter?: string;
  createdBefore?: string;
}

export const getAdminAnalysis = async () => {
  const token = localStorage.getItem("token");

  return fetch(`${APIEndpoint}/admin/analysis`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `${token || ""}`,
    },
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error creating API key:", error));
};

export const getCancellationFeedback = async (filters?: CancellationFeedbackFilters) => {
  const token = localStorage.getItem("token");
  
  const queryParams = new URLSearchParams();
  
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value.toString());
      }
    });
  }
  
  const url = `${APIEndpoint}/admin/cancellation-feedback${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `${token || ""}`,
    },
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error fetching cancellation feedback:", error));
};

export const getLastTradesByAccountId = async (accountId: number) => {
  const token = localStorage.getItem("token");

  return fetch(`${APIEndpoint}/admin/analysis/trades/${accountId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `${token || ""}`,
    },
  })
    .then((response) => response.json())
    .catch((error) => console.error("Error creating API key:", error));
};
